import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { SecurityService, WorkflowContextService } from 'src/app/services';
import {
  CommonTableComponent,
  TableColumn
} from 'src/app/components/common/common-table/common-table.component';
import { UserSettingsJanusComponent } from '../user-settings/user-settings-janus.component';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'wm-users-list',
  standalone: false,
  templateUrl: './users-list-janus.component.html',
  styleUrls: ['./users-list-janus.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UsersListJanusComponent {
  form: UntypedFormGroup;

  isSearching = false;
  hasSearched = false;

  /** 🔥 Overlay control */
  isUserSettingsOpen = false;

  /** 🔥 Selected user for settings */
  selectedUser: any = null;

  filtered: any[] = [];
  @ViewChild(CommonTableComponent)
  private commonTable!: CommonTableComponent;
  pageSize = 10;
  currentPage = 0;
  totalPages = 0;

  allUsers: any[] = [];

  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-users-list-janus';
  columns: TableColumn[] = [
    {
      key: 'select',
      label: 'Select',
      headerType: 'text',
      cellType: 'text',
      textColor: '#2196F3',
      width: '80px',
      expandable: true
      // hideable: true   // ✅ BLOCK hide/unhide
    },
    {
      key: 'name',
      label: 'Full Name',
      headerType: 'text',
      cellType: 'text',
      width: '220px',
      hideable: false // ✅ BLOCK hide/unhide
    },
    {
      key: 'userName',
      label: 'Username',
      headerType: 'text',
      cellType: 'text',
      width: '160px',
      hideable: false // ✅ BLOCK hide/unhide
    },
    {
      key: 'emailAddress',
      label: 'Email',
      headerType: 'text',
      cellType: 'text',
      width: '280px'
      // hideable: true   // ✅ BLOCK hide/unhide
    },
    {
      key: 'lastActivityDate',
      label: 'Last Activity Date',
      headerType: 'text',
      cellType: 'text',
      width: '180px'
      // hideable: true   // ✅ BLOCK hide/unhide
    }
  ];

  constructor(
    fb: UntypedFormBuilder,
    private securitySvc: SecurityService,
    private context: WorkflowContextService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.form = fb.group({
      searchText: ['', Validators.nullValidator]
    });

    this.form.controls.searchText.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => this.search());
  }
  UserSettingsJanusComponent = UserSettingsJanusComponent;

  // Deepak added code for sorting start 03/16/2026
  onSortChange(event: any) {
    const { sortField, sortDescending } = event;

    if (!sortField) {
      this.updatePagedData();
      return;
    }

    this.allUsers = [...this.allUsers].sort((a, b) => {
      if (a[sortField] < b[sortField]) return sortDescending ? 1 : -1;

      if (a[sortField] > b[sortField]) return sortDescending ? -1 : 1;

      return 0;
    });

    this.updatePagedData();
  }

  // Deepak added code for sorting end 03/16/2026
  ngOnInit() {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.pageSize = pageSize;
      }
    }
    this.route.queryParamMap.subscribe(params => {
      // Added by amisha on 2026-04-30
      // Reason: support both query-param and route-param edit states so inline expansion
      // stays in sync without losing the searched user context.
      const userId =
        params.get('edit') ?? this.route.snapshot.paramMap.get('userId');
      const searchText = (params.get('searchText') ?? '').trim();

      if ((this.form.controls.searchText.value ?? '').trim() !== searchText) {
        this.form.controls.searchText.setValue(searchText, {
          emitEvent: false
        });
        this.search(searchText, false);
      } else if (searchText && this.hasSearched) {
        this.restoreExpandedUserFromUrl();
      }

      if (userId) {
        this.restoreExpandedUserFromUrl();
      } else {
        this.closeUserSettingsUIOnly();
        this.closeInlineExpansion();
      }
    });
  }

  private closeUserSettingsUIOnly() {
    this.isUserSettingsOpen = false;
    this.selectedUser = null;
  }

  private openUserSettingsFromRoute(userId: string) {
    this.isUserSettingsOpen = true;

    this.securitySvc.getUser(userId).subscribe(fullUser => {
      this.selectedUser = fullUser;
    });
  }

  /** 🔍 SEARCH USERS */
  search(searchValue?: string, syncUrl: boolean = true) {
    const query = (
      searchValue ??
      this.form.get('searchText')?.value ??
      ''
    ).trim();

    if (syncUrl) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          searchText: query || null,
          edit: null
        },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }

    if (!query) {
      this.filtered = [];
      this.allUsers = [];
      this.isSearching = false;
      this.hasSearched = false;
      return;
    }

    this.isSearching = true;
    this.hasSearched = true;

    this.securitySvc
      .searchUsers(this.context.client, { searchText: query })
      .subscribe((users: any[]) => {
        this.isSearching = false;

        this.allUsers = users.map(u => ({
          ...u,
          select: 'Select',
          lastActivityDate: u.lastActivityDate ?? '—'
        }));

        this.totalPages = Math.ceil(this.allUsers.length / this.pageSize);
        this.currentPage = 0;

        this.updatePagedData();
        this.restoreExpandedUserFromUrl();
      });
  }
  updatePagedData() {
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;

    this.filtered = this.allUsers.slice(start, end);
  }
  onPageChange(page: number) {
    this.currentPage = page;
    this.updatePagedData();
  }
  /** 👉 OPEN USER SETTINGS IN OVERLAY (NO ROUTE, NO DIALOG) */
  onRowClick(user: any) {
    // Added by amisha on 2026-04-30
    // Reason: keep the list search/filter state intact by toggling only the inline edit query param.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { edit: user.id },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /** ❌ CLOSE OVERLAY */
  closeUserSettings() {
    this.closeUserSettingsUIOnly();

    // Added by amisha on 2026-04-30
    // Reason: close only the inline user settings state instead of navigating away
    // and clearing the current searched list context.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { edit: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  getUserSettingsInputs = (row: any) => {
    // Added by amisha on 2026-04-30
    // Reason: resolve the active inline user from either route style while the Janus list
    // moves toward query-param based expansion.
    const currentEditId =
      this.route.snapshot.queryParamMap.get('edit') ??
      this.route.snapshot.paramMap.get('userId');

    // ✅ navigate ONLY ON FIRST expansion
    if (currentEditId !== row.id) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { edit: row.id },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }

    return {
      row,
      // Added by amisha on 2026-04-30
      // Reason: push refreshed full-user values back into the visible row so enable/disable
      // and save actions update the inline list state without another search.
      onUserUpdated: (updatedUser: any) => {
        if (!updatedUser) {
          return;
        }

        Object.assign(row, {
          id: updatedUser.id,
          name: updatedUser.name,
          userName: updatedUser.userName,
          emailAddress: updatedUser.emailAddress,
          phoneNumber: updatedUser.phoneNumber,
          isEnabled: updatedUser.isEnabled,
          disabledOn: updatedUser.disabledOn,
          address: updatedUser.address
        });
      },
      closeExpansion: () => {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { edit: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });

        this.closeInlineExpansion();
      }
    };
  };

  closeInlineExpansion(): void {
    if (!this.commonTable) {
      return;
    }

    this.commonTable.closeExpansion();
  }

  private restoreExpandedUserFromUrl(): void {
    // Added by amisha on 2026-04-30
    // Reason: restore the expanded inline card after paging/search refreshes while supporting
    // both legacy route params and the current query-param edit state.
    const editId =
      this.route.snapshot.queryParamMap.get('edit') ??
      this.route.snapshot.paramMap.get('userId');
    if (!editId) {
      return;
    }

    const matchedIndex = this.allUsers.findIndex(user => user.id === editId);
    if (matchedIndex === -1) {
      return;
    }

    const requiredPage = Math.floor(matchedIndex / this.pageSize);
    if (this.currentPage !== requiredPage) {
      this.currentPage = requiredPage;
      this.updatePagedData();
    }

    const matchedUser = this.filtered.find(user => user.id === editId);
    if (!matchedUser) {
      return;
    }

    setTimeout(() => {
      if (!this.commonTable) {
        return;
      }
      this.commonTable.expandRow(matchedUser);
    });
  }

  onRowsPerPageChange(size: number): void {
    const pageSize = Number(size) || 10;

    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));

    this.pageSize = pageSize;
    this.currentPage = 0;

    this.updatePagedData();
  }
}
